import React, { useState } from 'react';
import { submitDirectMessage, checkDMRateLimit } from '../services/firestoreService';

const ContactFormSection = ({ profileId, username, isPremium, accentColor = '#749555', description }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    autoSubscribe: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [rateLimited, setRateLimited] = useState(false);

  // Get or create visitor ID for rate limiting
  const getVisitorId = () => {
    let visitorId = localStorage.getItem('chessweb_visitor_id');
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chessweb_visitor_id', visitorId);
    }
    return visitorId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (submitting) {
      // console.log('Already submitting, ignoring duplicate submit');
      return;
    }

    setError('');

    // Validation
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    try {
      // Check rate limit
      const visitorId = getVisitorId();
      const allowed = await checkDMRateLimit(profileId, visitorId);

      if (!allowed) {
        setRateLimited(true);
        setSubmitting(false);
        setError('You have reached the message limit. Please try again in an hour.');
        return;
      }

      // Submit message
      await submitDirectMessage(
        profileId,
        formData.email.trim(),
        formData.name.trim(),
        formData.message.trim(),
        formData.autoSubscribe
      );

      setSubmitted(true);

      // Reset form
      setFormData({
        name: '',
        email: '',
        message: '',
        autoSubscribe: true
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Not available for non-premium profiles
  if (!isPremium) {
    return null;
  }

  // Success state
  if (submitted) {
    return (
      <div className="bg-transparent rounded-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-green-500">
            <i className="fas fa-check text-green-400 text-3xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
          <p className="text-gray-200 mb-4">
            Delivered to {username || 'the profile owner'}.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="px-6 py-3 text-white rounded-lg font-medium transition-colors"
            style={{ backgroundColor: accentColor }}
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  // Rate limited state
  if (rateLimited && error) {
    return (
      <div className="bg-transparent rounded-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-orange-500">
            <i className="fas fa-hourglass-half text-orange-400 text-3xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Message Limit Reached</h3>

        </div>
      </div>
    );
  }

  // Contact form
  return (
    <div className="bg-transparent rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-white mb-2">
          <i className="fas fa-envelope mr-2" style={{ color: accentColor }}></i>
          Send a Message
        </h3>
        {description && (
          <p className="text-gray-300">{description}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name (Optional) */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-2">
            Name <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors bg-gray-800 text-white placeholder-gray-400"
            style={{ borderColor: accentColor }}
          />
        </div>

        {/* Email (Required) */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">
            Email <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors bg-gray-800 text-white placeholder-gray-400"
            style={{ borderColor: accentColor }}
          />
        </div>

        {/* Message (Required) */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-200 mb-2">
            Message <span className="text-red-400">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="5"
            placeholder="Your message..."
            className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors resize-none bg-gray-800 text-white placeholder-gray-400"
            style={{ borderColor: accentColor }}
          />
        </div>

        {/* Auto-subscribe Checkbox */}
        <div className="bg-purple-900/30 border-2 border-purple-500 rounded-lg p-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              name="autoSubscribe"
              checked={formData.autoSubscribe}
              onChange={handleChange}
              className="mt-1 w-4 h-4 text-purple-500 border-gray-600 rounded focus:ring-purple-500"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-white">
                Subscribe to updates
              </span>
              <p className="text-xs text-gray-300 mt-0.5">
                Get notified when {username || 'they'} publish new content.
              </p>
            </div>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-400 rounded-lg p-3 flex items-start">
            <i className="fas fa-exclamation-circle text-red-400 mt-0.5 mr-2"></i>
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className={`w-full px-6 py-3 text-white rounded-lg font-semibold transition-colors flex items-center justify-center ${
            submitting
              ? 'opacity-60 cursor-not-allowed'
              : 'hover:opacity-90'
          }`}
          style={{ backgroundColor: accentColor }}
        >
          {submitting ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Sending...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane mr-2"></i>
              Send Message
            </>
          )}
        </button>

        {/* Rate Limit Info */}
      
      </form>
    </div>
  );
};

export default ContactFormSection;
